import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { lbsRangeToKg, inRangeToCm } from "@/lib/units";
import {
  Lock, Sparkles, X, ArrowLeft, Scale, Zap, Heart, Scissors,
} from "lucide-react";

/**
 * Compare page — side-by-side up to 3 breeds.
 * Reads breed IDs from ?ids=a,b,c URL param.
 * Locked behind the $2 Unlock — non-premium users see a paywall.
 */

// ---------- Row definitions ----------
// Each row = one attribute compared across all selected breeds.
// `key` is the field on the breed record, `render` renders that value.
const RATING_ICONS = { Energy: Zap, Trainability: Heart, "Grooming needs": Scissors };

function ratingBar(value) {
  const v = Math.max(0, Math.min(5, value || 0));
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`h-2 w-4 rounded-full ${n <= v ? "bg-primary" : "bg-muted"}`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{v}/5</span>
    </div>
  );
}

const ROWS = [
  { section: "Basics" },
  { label: "Group", get: (b) => <span className="text-accent font-semibold">{b.group}</span> },
  { label: "Size", get: (b) => <span className="capitalize">{b.size}</span> },
  { label: "Weight", get: (b) => `${lbsRangeToKg(b.weight_lbs)} kg` },
  { label: "Height", get: (b) => `${inRangeToCm(b.height_in)} cm` },
  { label: "Lifespan", get: (b) => `${b.lifespan_years} y` },

  { section: "Temperament" },
  { label: "Energy", get: (b) => ratingBar(b.energy) },
  { label: "Trainability", get: (b) => ratingBar(b.trainability) },
  { label: "Barking", get: (b) => ratingBar(b.barking) },

  { section: "Care" },
  { label: "Grooming", get: (b) => ratingBar(b.grooming) },
  { label: "Shedding", get: (b) => ratingBar(b.shedding) },
  { label: "Allergy friendly", get: (b) => ratingBar(b.allergy_friendly) },

  { section: "Household fit" },
  { label: "Good with kids", get: (b) => ratingBar(b.good_with_kids) },
  { label: "Good with other pets", get: (b) => ratingBar(b.good_with_pets) },
  { label: "Apartment ok", get: (b) => ratingBar(b.apartment_ok) },
  { label: "Hours alone ok", get: (b) => ratingBar(b.hours_alone_ok) },
  { label: "Beginner friendly", get: (b) => ratingBar(b.novice_ok) },

  { section: "Personality" },
  {
    label: "Traits",
    get: (b) => (
      <div className="flex flex-wrap gap-1">
        {b.temperament.map((t) => (
          <span key={t} className="rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">{t}</span>
        ))}
      </div>
    ),
  },
];

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const { unlocked, loading: entLoading } = useEntitlement();
  const navigate = useNavigate();

  const idsFromUrl = useMemo(
    () => (params.get("ids") || "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, 3),
    [params]
  );
  const [breeds, setBreeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (idsFromUrl.length === 0) { setBreeds([]); setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          idsFromUrl.map((id) =>
            axios.get(`${API}/breeds/${id}`).then((r) => r.data).catch(() => null)
          )
        );
        setBreeds(results.filter(Boolean));
      } finally {
        setLoading(false);
      }
    })();
  }, [idsFromUrl.join(",")]); // eslint-disable-line

  // Paywall for locked devices. Wait for the entitlement check to avoid a flash.
  if (entLoading) return null;
  if (!unlocked) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-24 pb-24 text-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
          <Lock size={12} /> Unlock-only feature
        </span>
        <h1 className="mt-6 font-display font-bold text-4xl md:text-5xl text-accent leading-tight">
          Compare breeds side-by-side.
        </h1>
        <p className="mt-4 text-muted-foreground">
          Line up 2 or 3 breeds and see every trait, cost, and household fit next to each other. Included with your $2 Unlock.
        </p>
        <Link
          to="/upgrade"
          data-testid="compare-unlock-cta"
          className="pill-btn mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-semibold text-base"
        >
          <Sparkles size={16} /> Unlock for $2
        </Link>
      </div>
    );
  }

  const remove = (id) => {
    const next = idsFromUrl.filter((x) => x !== id);
    if (next.length) setParams({ ids: next.join(",") });
    else navigate("/breeds");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link to="/breeds" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent">
        <ArrowLeft size={14} /> Back to breeds
      </Link>
      <div className="mt-6 mb-8">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Unlocked feature</p>
        <h1 className="mt-2 font-display font-bold text-4xl md:text-5xl text-accent tracking-tight">Breed comparison</h1>
        <p className="mt-3 text-muted-foreground max-w-xl">
          Pick up to 3 breeds and see every attribute lined up. Add breeds by opening any profile → "Add to compare".
        </p>
      </div>

      {(() => {
        if (loading) return <div className="text-muted-foreground">Loading…</div>;
        if (breeds.length === 0) return <EmptyState />;
        return (
        <div className="overflow-x-auto -mx-6 lg:-mx-10 px-6 lg:px-10">
          <table
            className="w-full border-collapse table-fixed"
            style={{ minWidth: (breeds.length + (breeds.length < 3 ? 1 : 0)) * 220 + 160 }}
            data-testid="compare-table"
          >
            <colgroup>
              <col style={{ width: 160 }} />
              {breeds.map((b) => <col key={b.id} style={{ width: `calc((100% - 160px) / ${breeds.length + (breeds.length < 3 ? 1 : 0)})` }} />)}
              {breeds.length < 3 && <col style={{ width: `calc((100% - 160px) / ${breeds.length + 1})` }} />}
            </colgroup>
            {/* Column headers — breed cards */}
            <thead>
              <tr>
                <th className="text-left align-bottom pr-4 pb-4" />
                {breeds.map((b) => (
                  <th key={b.id} className="pr-4 pb-4 align-top">
                    <div className="relative rounded-2xl bg-card border border-border overflow-hidden">
                      <div className="aspect-[4/3] bg-muted overflow-hidden">
                        <img src={b.image} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <button
                        onClick={() => remove(b.id)}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-background/90 grid place-items-center hover:bg-background"
                        aria-label={`Remove ${b.name}`}
                        data-testid={`compare-remove-${b.id}`}
                      >
                        <X size={14} />
                      </button>
                      <div className="p-3 text-left">
                        <Link to={`/breeds/${b.id}`} className="font-display font-bold text-lg text-accent hover:underline block truncate">
                          {b.name}
                        </Link>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{b.summary}</p>
                      </div>
                    </div>
                  </th>
                ))}
                {breeds.length < 3 && (
                  <th className="pr-4 pb-4 align-top">
                    <Link
                      to="/breeds"
                      className="flex flex-col items-center justify-center h-full min-h-[220px] rounded-2xl border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <Scale size={24} className="mb-2" />
                      <span className="text-sm">Add another breed</span>
                    </Link>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) =>
                row.section ? (
                  <tr key={`sec-${i}`}>
                    <td
                      colSpan={breeds.length + (breeds.length < 3 ? 2 : 1)}
                      className="pt-8 pb-2 text-xs uppercase tracking-wider font-bold text-primary"
                    >
                      {row.section}
                    </td>
                  </tr>
                ) : (
                  <tr key={row.label} className="border-t border-border/60">
                    <td className="py-3 pr-4 text-sm text-muted-foreground font-medium align-middle">{row.label}</td>
                    {breeds.map((b) => (
                      <td key={b.id} className="py-3 pr-4 text-sm align-middle">
                        {row.get(b)}
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        );
      })()}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border-2 border-dashed border-border p-10 text-center">
      <Scale size={28} className="mx-auto text-muted-foreground" />
      <h2 className="mt-4 font-display font-bold text-xl text-accent">Pick some breeds to compare</h2>
      <p className="mt-2 text-muted-foreground text-sm">Open any breed profile and tap "Add to compare", or head to Browse.</p>
      <Link
        to="/breeds"
        className="mt-6 inline-flex items-center rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold"
      >
        Browse breeds
      </Link>
    </div>
  );
}
